import { useMutation, useQueryClient } from '@tanstack/react-query';

import type {
  PatchAdminV1OrgStoresIdBody,
  PostAdminV1OrgStoresBody,
} from '@nucleus-commerce/api-client/admin';

import { session } from '@/shared/session/session';

import { storesApi } from '../api/stores-api';

import { storeKeys } from './store-keys';

/** The sidebar switcher reads stores from /me, so store changes refresh the session too. */
function refreshAll(queryClient: ReturnType<typeof useQueryClient>) {
  return Promise.all([
    queryClient.invalidateQueries({ queryKey: storeKeys.all }),
    queryClient.invalidateQueries({ queryKey: session.queryKey }),
  ]);
}

export function useCreateStore() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: PostAdminV1OrgStoresBody) => storesApi.create(body),
    meta: { successMessage: 'stores:toasts.created', silentError: true },
    onSuccess: (store) => {
      queryClient.setQueryData(storeKeys.detail(store.id), store);
      return refreshAll(queryClient);
    },
  });
}

/** Screens that save one settings group (tax) pass their own toast. */
export function useUpdateStore(id: string, successMessage = 'stores:toasts.updated') {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: PatchAdminV1OrgStoresIdBody) => storesApi.update(id, body),
    meta: { successMessage, silentError: true },
    onSuccess: (store) => {
      queryClient.setQueryData(storeKeys.detail(id), store);
      return refreshAll(queryClient);
    },
  });
}

/** Only draft stores can be deleted; the page explains a 409. */
export function useDeleteStore() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => storesApi.remove(id),
    meta: { successMessage: 'stores:toasts.deleted', silentError: true },
    onSuccess: (_void, id) => {
      queryClient.removeQueries({ queryKey: storeKeys.detail(id) });
      return refreshAll(queryClient);
    },
  });
}
